import { useCallback, useEffect, useRef, useState } from "react";
import { Button, ConfirmDialog, Field } from "../../components";
import { ApiError, describeApiError } from "../../api/errors";
import { cleanCode, stepUpSink, verifyMfa } from "../../api/mfa";
import { t } from "../../i18n/t";

/**
 * xmg-sec-mfa (S3a step-up): mounted once in the signed-in layout. When a sensitive action (connection revoke,
 * member or role changes, sign-off, new recovery codes) comes back `403 mfa_step_up_required`, `withStepUp`
 * calls the prompt this component registers: it asks for a fresh code, passes it to `POST /api/v1/auth/mfa/verify`,
 * and resolves `true` so the action is retried once - or `false` on cancel, which leaves the original error to
 * the screen that made the call.
 */
export function StepUpDialog(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((passed: boolean) => void) | null>(null);

  const settle = useCallback((passed: boolean) => {
    resolver.current?.(passed);
    resolver.current = null;
    setOpen(false);
    setCode("");
    setError(null);
    setUseRecovery(false);
  }, []);

  useEffect(() => {
    stepUpSink.prompt = () =>
      new Promise<boolean>((resolve) => {
        // A second prompt while one is open answers the first as cancelled rather than leaving it hanging.
        resolver.current?.(false);
        resolver.current = resolve;
        setOpen(true);
      });
    return () => {
      stepUpSink.prompt = null;
      resolver.current?.(false);
      resolver.current = null;
    };
  }, []);

  async function onConfirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await verifyMfa(useRecovery ? { recovery_code: code.trim() } : { code: cleanCode(code) });
      settle(true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.is("mfa_locked")) {
        setError(t("mfa.challenge.locked"));
      } else if (caught instanceof ApiError && caught.is("rate_limited")) {
        const seconds = caught.details.retry_after_seconds;
        setError(t("mfa.challenge.throttled", { seconds: typeof seconds === "number" ? seconds : 60 }));
      } else if (caught instanceof ApiError && caught.is("mfa_invalid_code")) {
        setError(useRecovery ? t("mfa.challenge.recovery_invalid") : t("mfa.challenge.invalid"));
      } else {
        setError(describeApiError(caught, t("error.unexpected")));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog
      open={open}
      title={t("mfa.stepup.title")}
      confirmLabel={t("mfa.stepup.confirm")}
      onConfirm={() => void onConfirm()}
      onCancel={() => settle(false)}
      confirmDisabled={busy || code.trim() === ""}
    >
      <p>{t("mfa.stepup.body")}</p>
      <Field
        label={useRecovery ? t("mfa.challenge.recovery_label") : t("mfa.challenge.code_label")}
        error={error}
      >
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            inputMode={useRecovery ? "text" : "numeric"}
            autoComplete="one-time-code"
            className="mono"
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        )}
      </Field>
      <Button
        size="compact"
        onClick={() => {
          setUseRecovery((prev) => !prev);
          setCode("");
          setError(null);
        }}
      >
        {useRecovery ? t("mfa.challenge.use_app") : t("mfa.challenge.use_recovery")}
      </Button>
    </ConfirmDialog>
  );
}
